import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";

/**
 * Minimal, portal-rendered modal dialog. Used for short "create
 * something" flows like the Data Sheets and Schedules entry screens,
 * where we want a focused surface with a title + form + footer
 * actions and nothing else from the underlying page distracting.
 *
 * Behaviour:
 *  - ESC closes the dialog (disabled while `busy`).
 *  - Clicking the backdrop does NOT close — the user must use Cancel
 *    or ESC. This is intentional to prevent accidental dismissal of
 *    half-typed input when the mouse slips outside the dialog.
 *  - When open, body scrolling is locked and focus is moved into the
 *    dialog's first focusable element.
 *
 * Accessibility:
 *  - `role="dialog"` + `aria-modal="true"` + `aria-labelledby` via the
 *    dialog title's id.
 */
export interface ModalProps {
  open: boolean;
  onClose: () => void;
  title: string;
  /** Visible in the top-right header for users who prefer the mouse;
   *  default true. */
  showCloseButton?: boolean;
  /** Renders below the title/body separator. Typically the footer
   *  buttons row ("Cancel" / primary CTA). */
  footer?: React.ReactNode;
  /** Set to true while an async action is running; suppresses ESC-close
   *  and can be forwarded to the footer buttons so the whole surface
   *  reads as "busy". (Backdrop clicks never close this dialog.) */
  busy?: boolean;
  /** Width cap for the dialog body. Small "create" forms look best at
   *  the default 480px; larger editors pass a wider value. */
  maxWidth?: number;
  children: React.ReactNode;
}

export function Modal({
  open,
  onClose,
  title,
  showCloseButton = true,
  footer,
  busy = false,
  maxWidth = 480,
  children,
}: ModalProps) {
  const dialogRef = useRef<HTMLDivElement | null>(null);
  const titleId = useRef(`modal-title-${Math.random().toString(36).slice(2)}`);

  // ESC handler + body scroll lock. Kept inside the open guard so
  // closed modals don't leave stray listeners around; React cleans up
  // the effect when `open` flips or the component unmounts.
  useEffect(() => {
    if (!open) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape" && !busy) {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", onKeyDown);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKeyDown);
      document.body.style.overflow = prevOverflow;
    };
  }, [open, busy, onClose]);

  // Move focus into the dialog when it opens, falling back to the
  // dialog surface itself so screen readers still announce it.
  useEffect(() => {
    if (!open) return;
    const root = dialogRef.current;
    if (!root) return;
    const first = root.querySelector<HTMLElement>(
      "input, select, textarea, button, [tabindex]:not([tabindex='-1'])",
    );
    (first ?? root).focus({ preventScroll: true });
  }, [open]);

  if (!open) return null;

  return createPortal(
    <div className="portal-modal-backdrop" aria-hidden="false">
      {/* Clicks on the backdrop deliberately do NOT close the dialog.
          The user must explicitly press Cancel or ESC so that stray
          clicks outside the dialog don't throw away unsaved input. */}
      <div
        ref={dialogRef}
        className="portal-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId.current}
        tabIndex={-1}
        style={{ maxWidth }}
      >
        <header className="portal-modal-head">
          <h3 id={titleId.current} style={{ margin: 0 }}>
            {title}
          </h3>
          {showCloseButton && (
            <button
              type="button"
              className="portal-modal-close"
              onClick={() => {
                if (!busy) onClose();
              }}
              disabled={busy}
              aria-label="Close"
              title="Close"
            >
              <X size={18} />
            </button>
          )}
        </header>
        <div className="portal-modal-body">{children}</div>
        {footer && <footer className="portal-modal-foot">{footer}</footer>}
      </div>
    </div>,
    document.body,
  );
}
